import { useState, useEffect, useCallback, useRef } from 'react';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-script';
const TURNSTILE_SCRIPT_URL = 'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

/**
 * Hook per Cloudflare Turnstile.
 *
 * Carica la configurazione dal backend (/api/auth/captcha-config/),
 * inietta lo script Turnstile solo se abilitato, e gestisce
 * render/reset del widget.
 *
 * @returns {{ enabled, loading, token, resetTurnstile, TurnstileWidget }}
 */
export default function useTurnstile() {
  const [config, setConfig] = useState({ enabled: false, site_key: '' });
  const [loading, setLoading] = useState(true);
  const [token, setToken] = useState('');
  const widgetIdRef = useRef(null);
  const containerRef = useRef(null);

  // 1. Fetch config dal backend
  useEffect(() => {
    const API_BASE = process.env.REACT_APP_API_URL || '';
    fetch(`${API_BASE}/api/auth/captcha-config/`)
      .then((r) => r.json())
      .then((data) => {
        setConfig(data);
        setLoading(false);
      })
      .catch(() => {
        setConfig({ enabled: false, site_key: '' });
        setLoading(false);
      });
  }, []);

  // 2. Carica script Turnstile (solo se abilitato)
  useEffect(() => {
    if (!config.enabled || !config.site_key) return;
    if (document.getElementById(TURNSTILE_SCRIPT_ID)) return;

    const script = document.createElement('script');
    script.id = TURNSTILE_SCRIPT_ID;
    script.src = TURNSTILE_SCRIPT_URL;
    script.async = true;
    document.head.appendChild(script);
  }, [config.enabled, config.site_key]);

  // 3. Render widget quando il container è pronto
  const setContainerRef = useCallback(
    (node) => {
      containerRef.current = node;
      if (!node || !config.enabled || !config.site_key) return;

      const tryRender = () => {
        if (!window.turnstile) {
          setTimeout(tryRender, 200);
          return;
        }
        if (widgetIdRef.current != null) {
          try { window.turnstile.remove(widgetIdRef.current); } catch {}
        }
        widgetIdRef.current = window.turnstile.render(node, {
          sitekey: config.site_key,
          callback: (t) => setToken(t),
          'expired-callback': () => setToken(''),
          'error-callback': () => setToken(''),
          theme: 'light',
          size: 'flexible',
        });
      };
      tryRender();
    },
    [config.enabled, config.site_key],
  );

  // 4. Reset (utile dopo errore di submit)
  const resetTurnstile = useCallback(() => {
    setToken('');
    if (widgetIdRef.current != null && window.turnstile) {
      try { window.turnstile.reset(widgetIdRef.current); } catch {}
    }
  }, []);

  // 5. Componente widget inline
  const TurnstileWidget = config.enabled ? (
    <div ref={setContainerRef} style={{ minHeight: 65 }} />
  ) : null;

  return { enabled: config.enabled, loading, token, resetTurnstile, TurnstileWidget };
}
